/**
 * @file useGenerateContent.ts
 * @description Custom hook for generating content.
 * Handles the state management and API calls for content generation.
 */

import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { GenerateContentFormData, ContentDetails } from "../types";
import { auth } from "../config/firebaseConfig";
import config from "../config/config";
import log from "../config/logging";

const useGenerateContent = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const generateContent = useCallback(
    async (
      data:
        | GenerateContentFormData
        | (ContentDetails & { contentDetailId: string }),
      onComplete?: () => void
    ) => {
      const payload = {
        contentDetailId:
          "contentDetailId" in data ? data.contentDetailId : undefined,
        website: data.website,
        contentType: data.contentType,
        clientInfo: data.clientInfo,
        keyword: data.keyword,
        relatedKeywords: Array.isArray(data.relatedKeywords)
          ? data.relatedKeywords.filter((kw) => kw.trim() !== "")
          : [],
        peopleAlsoAsk: Array.isArray(data.peopleAlsoAsk)
          ? data.peopleAlsoAsk.filter((q) => q.trim() !== "")
          : [],
        title: data.title || "",
      };

      log.debug("Starting SEO content generation", payload);
      setIsLoading(true);

      try {
        const user = auth.currentUser;
        if (!user) {
          throw new Error("User not authenticated");
        }

        const token = await user.getIdToken(true);
        if (!token) {
          throw new Error("Token not found");
        }

        const endpoint =
          "contentDetailId" in data
            ? `${config.backendUrl}/api/contents/generate`
            : `${config.backendUrl}/api/contents`;

        const response = await fetch(endpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        });

        type ContentResponse = {
          success?: boolean;
          contentDetailId?: string;
          message?: string;
          errorCode?: string;
        } | null;

        const rawBody = await response.text();
        let responseData: ContentResponse = null;
        try {
          responseData = rawBody ? JSON.parse(rawBody) : null;
        } catch (parseError) {
          log.warn("Unable to parse content generation response", {
            parseError,
          });
        }

        if (response.status === 403) {
          const limitMessage =
            responseData?.message || "Content generation limit reached";
          throw new Error(limitMessage);
        }

        if (!response.ok) {
          // Use the specific error message from the backend if available
          const errorMessage =
            responseData?.message ||
            (responseData?.errorCode === "INVALID_URL"
              ? "L'URL indicato non risulta raggiungibile. Controlla che sia corretto o copialo dalla barra del browser."
              : responseData?.errorCode === "MISSING_REQUIRED_FIELD"
                ? "Compila la Keyword principale per tutte le righe prima di lanciare la generazione."
                : `Errore: ${response.statusText}`);
          const serverError = new Error(errorMessage);
          (serverError as Error & { code?: string }).code =
            responseData?.errorCode;
          throw serverError;
        }

        log.debug("SEO content generation successful", { responseData });

        if (responseData?.success && responseData.contentDetailId) {
          if (onComplete) {
            onComplete();
          } else {
            navigate(`/contenuto/${responseData.contentDetailId as string}`);
          }
        } else {
          toast.warning(
            responseData?.message || "Nessun contenuto generato"
          );
        }
      } catch (error: unknown) {
        if (
          error instanceof Error &&
          error.message === "Content generation limit reached"
        ) {
          toast.error(
            "Hai raggiunto il limite di generazione dei contenuti per questo mese."
          );
        } else if (error instanceof Error) {
          log.error("Error during SEO content generation", { error });
          // Display the specific error message from the backend
          const errorMessage = error.message || "Errore durante la generazione";
          toast.error(errorMessage);
        } else {
          log.error("Error during SEO content generation", { error });
          toast.error("Errore durante la generazione del contenuto");
        }
      } finally {
        setIsLoading(false);
        log.debug("SEO content generation process completed");
      }
    },
    [navigate]
  );

  return { generateContent, isLoading, setIsLoading };
};

export default useGenerateContent;
